import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { verifyMagicLink } from '@/api/auth';
import { useAuthStore } from '@/store/authStore';

/**
 * Verifies the magic-link token from the URL, stores the session JWT,
 * routes into the console. A failed verification renders a clear error
 * with a button back to /login.
 */
export function VerifyScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore(s => s.setSession);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setError('No token in URL — the link may have been truncated.');
      return;
    }
    verifyMagicLink(token)
      .then(r => {
        setSession(r.token, r.admin);
        // Land on the admin home after sign-in. The router redirects
        // any unknown authed path to / so this is the canonical home.
        navigate('/', { replace: true });
      })
      .catch(err => {
        const msg =
          err?.response?.data?.error?.message ||
          err?.message ||
          'Could not verify the sign-in link.';
        setError(msg);
      });
  }, [params, navigate, setSession]);

  return (
    <div className="grid min-h-screen place-items-center bg-nurev-bg px-4">
      <div className="w-full max-w-md text-center">
        {error ? (
          <>
            <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-400" />
            <h1 className="text-lg font-semibold text-nurev-text">
              Sign-in failed
            </h1>
            <p className="mt-2 text-sm text-nurev-text-soft">{error}</p>
            <button
              className="btn-primary mt-6"
              onClick={() => navigate('/login', { replace: true })}
            >
              Request a new link
            </button>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto mb-3 h-10 w-10 animate-spin text-nurev-purple-light" />
            <p className="text-sm text-nurev-text-soft">
              Verifying your sign-in link…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
